/**
 * Direct fetch utility for Supabase REST API
 * Bypasses the stuck Supabase client by using direct HTTP calls
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Gets the access token from localStorage
 */
export const getAccessToken = (): string => {
  if (!SUPABASE_URL) return SUPABASE_KEY || '';
  
  const sessionKey = `sb-${SUPABASE_URL.split('//')[1].split('.')[0]}-auth-token`;
  const storedSession = localStorage.getItem(sessionKey);
  
  if (storedSession) {
    try {
      const sessionData = JSON.parse(storedSession);
      if (sessionData.access_token) {
        return sessionData.access_token;
      }
    } catch (e) {
      console.warn('[supabaseFetch] Error parsing session, using anon key:', e);
    }
  }
  
  return SUPABASE_KEY || '';
};

/**
 * Makes a direct fetch call to Supabase REST API
 */
export const supabaseFetch = async (
  table: string,
  options: {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    select?: string;
    filter?: string;
    order?: string;
    body?: any;
    headers?: Record<string, string>;
  } = {}
): Promise<{ data: any; error: any }> => {
  const {
    method = 'GET',
    select = '*',
    filter = '',
    order = '',
    body,
    headers = {},
  } = options;

  const accessToken = getAccessToken();
  
  // Log for debugging (can be removed later)
  if (table === 'corporate_users' || table === 'corporate_rooms' || table === 'corporate_invites') {
    console.log(`[supabaseFetch] Fetching ${table}:`, {
      method,
      hasToken: !!accessToken,
      tokenLength: accessToken?.length || 0,
      filter,
      order,
      select
    });
  }
  
  // Build URL with filters, select, and order
  let url = `${SUPABASE_URL}/rest/v1/${table}`;
  const params: string[] = [];
  if (select !== '*') {
    params.push(`select=${encodeURIComponent(select)}`);
  }
  if (filter) {
    // Filter might already contain multiple params joined with &
    params.push(filter);
  }
  if (order) {
    params.push(`order=${encodeURIComponent(order)}`);
  }
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'apikey': SUPABASE_KEY || '',
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }
      return {
        data: null,
        error: {
          message: errorData.message || errorData.error_description || `HTTP ${response.status}`,
          status: response.status,
          code: errorData.code || String(response.status),
        },
      };
    }

    const data = await response.json();
    
    // Log for debugging (can be removed later)
    if (table === 'corporate_users' || table === 'corporate_rooms' || table === 'corporate_invites') {
      console.log(`[supabaseFetch] ${table} response:`, {
        isArray: Array.isArray(data),
        length: Array.isArray(data) ? data.length : 'N/A',
        type: typeof data,
        data: data
      });
    }
    
    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message: error instanceof Error ? error.message : 'Unknown error',
        status: 0,
        code: 'FETCH_ERROR',
      },
    };
  }
};

/**
 * Helper to build filter query string for Supabase REST API
 */
export const buildFilter = (filters: Record<string, any>): string => {
  const parts: string[] = [];
  
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;
    
    if (Array.isArray(value)) {
      parts.push(`${key}=in.(${value.join(',')})`);
    } else if (typeof value === 'object' && value.operator) {
      // Support operators like { operator: 'eq', value: 'something' }
      parts.push(`${key}=${value.operator}.${value.value}`);
    } else {
      parts.push(`${key}=eq.${value}`);
    }
  }
  
  return parts.join('&');
};

